import {
    equalTo,
    getDatabase,
    off,
    onValue,
    orderByChild,
    Query,
    query,
    ref,
} from 'firebase/database'
import { useEffect } from 'react'
import { IStationState, OrderStatesEnum } from '../constants/order'
import { selectLicense } from '../redux/reducers/license'
import { clearOrders, setStateOrders, setStationStateOrders } from '../redux/reducers/order'
import { stationSelectors } from '../redux/reducers/station'
import { IFirebaseOrder, IOrder } from '../types/order'
import { useAppDispatch, useAppSelector } from './redux'

export const useFirebaseOrdersConnect = (
    stationStates?: IStationState[],
    states?: OrderStatesEnum[]
) => {
    const { token } = useAppSelector(selectLicense)!
    const { code } = useAppSelector(stationSelectors.currentStation)!

    const dispatch = useAppDispatch()

    useEffect(() => {
        const db = getDatabase()

        const listeners: {
            listener: Query
            stationState?: IStationState
            state?: OrderStatesEnum
        }[] = []

        if (!states || stationStates) {
            stationStates?.forEach((stationState) => {
                const listener = query(
                    ref(db, `${token}/orders`),
                    orderByChild(`digibon_kitchens/${code}`),
                    equalTo(stationState)
                )

                listeners.push({ listener, stationState })
            })
        } else {
            states?.forEach((state) => {
                const listener = query(
                    ref(db, `${token}/orders`),
                    orderByChild('state'),
                    equalTo(state)
                )

                listeners.push({ listener, state })
            })
        }

        listeners.forEach(({ listener, stationState, state }) => {
            onValue(listener, (snapshot) => {
                const orders: IOrder[] = []

                snapshot.forEach((childSnapshot) => {
                    const order = childSnapshot.val() as IFirebaseOrder

                    if (!order.code) return

                    orders.push({
                        orderKey: childSnapshot.key!,
                        orderId: order.order_id,
                        code: order.code,
                        isAddressRequired: order.address_required,
                        dateTime: {
                            type: order.delivery_time_type,
                            since: order.delivery_time_since,
                            until: order.delivery_time_until,
                            date: order.delivery_date,
                            finishedAt: order.finished_at,
                            driverPickupTime: order.driver_pickup_time,
                        },
                        customer: {
                            name: order.customer.name,
                            phone: order.customer.phone,
                            address: order.customer.address?.full_address,
                            street: order.customer.address?.street,
                            town: order.customer.address?.town,
                        },
                        zone: {
                            name: order.zone_name,
                            driverName: order.delivery_state?.driver_name,
                        },
                        products: Object.values(order.digibon_products).map((product) => ({
                            orderKey: childSnapshot.key!,
                            id: product.product_id,
                            name: product.name,
                            digibonStation: product.digibon_station,
                            isPreparable:
                                order.digibon_kitchens?.[code] === OrderStatesEnum.IN_PROCESS &&
                                product.digibon_station === code,
                            totalQuantity: product.quantity,
                            readyQuantity: product.ready_quantity,
                            addedIngredients: product.added_ingredients,
                            removedIngredients: product.removed_ingredients,
                        })),
                        source: order.source,
                        stations: order.digibon_kitchens!,
                        state: order.state,
                        note: order.note,
                    })
                })

                if (stationState) {
                    dispatch(
                        setStationStateOrders({
                            stationState,
                            orders,
                        })
                    )

                    return
                }

                dispatch(
                    setStateOrders({
                        state: state!,
                        orders,
                    })
                )
            })
        })

        return () => {
            listeners.forEach(({ listener }) => {
                off(listener)
            })

            dispatch(clearOrders())
        }
    }, [stationStates, states, token, code, dispatch])
}
